import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Divider, Stack } from "@mui/material";
import { QueryInfo, QueryField, socialUniverseField } from "system/query/QueryInfo";
import { SimpleFormLabel } from "components/dialogs/SimpleFormDialog";
import { Agg } from "system/query/Aggs";
import LeafAggSelector from "components/widgetParts/LeafAggSelector";
import TabbedDialog from "components/dialogs/TabbedDialog";
import SplitSettingsWrapper from "components/settingsConfig/SplitSettingsWrapper";
import NumericCardPreview from "./NumericCardPreview";
import SettingsOptions from "components/settingsConfig/SettingsOptions";
import SettingsWidgetTitleSelect from "components/settingsConfig/uiComponents/SettingsWidgetTitleSingleSelect";
import SettingsAccordion from "components/settingsConfig/uiComponents/SettingsAccordion";
import SettingsTextField from "components/settingsConfig/uiComponents/SettingsTextField";
import SettingsToggleButton, { horizontalAlignmentOptions } from "components/settingsConfig/uiComponents/SettingsToggleButton";
import SettingsColorPicker from "components/settingsConfig/uiComponents/SettingsColorPicker";
import SettingsCheckbox from "components/settingsConfig/uiComponents/SettingsCheckbox";
import { AutoWidgetQueryBehavior, getSingleOriginalRequestByName, WidgetQuery, WidgetQueryBehavior, WidgetRequest, WidgetTemplateQueryBehavior } from "system/widget/Widget";
import { WidgetSettingsProps } from "components/widgetParts/WidgetSettingsWrapper";
import QueryWidgetSettingsMissingQueryInfoAlert from "components/widgetParts/queryWidgetComposition/QueryWidgetSettingsMissingQueryInfoAlert";
import MultiQueryRequestSettings, { MultiQueryRequestSettingsPresets } from "components/widgetParts/MultiQueryRequestSettings";

export type NumericCardSettingsStateType = {
  abbreviate: boolean;
  alignment: string;
  color?: string;
  customWidgetTitle: string;
  showDecimals: boolean;
  widgetTitle: string;
  widgetTitleAlignment: string;
}

export const defaultSettings: NumericCardSettingsStateType = {
  abbreviate: false,
  alignment: 'left',
  color: undefined,
  customWidgetTitle: '',
  showDecimals: false,
  widgetTitle: 'default',
  widgetTitleAlignment: 'left',
}

const defaultWidgetRequest = {
  name: 'numeric-card',
  endpoint: 'query/agg',
  queryParams: {
    flatten: true,
    include_total: true
  },
  autoWidgetQueryBehavior: AutoWidgetQueryBehavior.AUTO_SINGLE
} as WidgetRequest;

export function BuildDefaultNumericCardRequests(queryInfo: QueryInfo): WidgetRequest[] | undefined {
  const foundSocialUniverseField = queryInfo.fields?.find((field: QueryField) => field.id === socialUniverseField.id) || false;

  const widgetRequest : WidgetRequest = { ...defaultWidgetRequest };

  const requestInitBody = { aggs: {} };
  if (foundSocialUniverseField) {
    requestInitBody.aggs = {
      [socialUniverseField.label]: {
        op: socialUniverseField.id
      }
    };
  }
  widgetRequest.requestInit = { body: requestInitBody };
  return [widgetRequest];
}

export default function NumericCardSettings(props: WidgetSettingsProps): JSX.Element {
  const { i18n } = useTranslation();

  const originalRequest = getSingleOriginalRequestByName(props.activeWidgetRequests, 'numeric-card');
  const requestBody = originalRequest?.requestInit?.body || {};

  const updatedDefaultSettings = {...defaultSettings, ...props?.widget?.data?.settings};
  const [settingsState, setSettingsState] = useState<NumericCardSettingsStateType>(updatedDefaultSettings);
  const settingsOnChange = (updatedState: NumericCardSettingsStateType) => setSettingsState(updatedState);

  const [autoWidgetQueryBehavior, setAutoWidgetQueryBehavior] = useState<AutoWidgetQueryBehavior>(originalRequest?.autoWidgetQueryBehavior || AutoWidgetQueryBehavior.CUSTOM);
  const [widgetQueryBehavior, setWidgetQueryBehavior] = useState<WidgetQueryBehavior>(originalRequest?.widgetQueryBehavior || WidgetQueryBehavior.SINGLE_INTERSECTION);
  const [widgetTemplateQueryBehavior, setWidgetTemplateQueryBehavior] = useState<WidgetTemplateQueryBehavior>(originalRequest?.widgetTemplateQueryBehavior || WidgetTemplateQueryBehavior.SINGLE_COMBINED);

  const keys = Object.keys(requestBody?.aggs || {});
  let firstAggKey = "";
  let aggDefinition = null;
  if (keys.length) {
    firstAggKey = keys[0];
    aggDefinition = requestBody.aggs[firstAggKey];
  }

  const [aggsLabel, setAggsLabel] = useState<string>(firstAggKey);
  const [localAggs, setLocalAggs] = useState<Agg>(aggDefinition);

  // check if the data object has the fields needed
  const updateAggs = () => {
    const label:string = aggsLabel || localAggs?.field || "total";

    const widgetRequest : WidgetRequest = { ...defaultWidgetRequest };
    const requestInitBody = { aggs: {} };
    if (localAggs) {
      requestInitBody.aggs = { [label]: localAggs };
    }
    widgetRequest.requestInit = { body: requestInitBody };

    widgetRequest.autoWidgetQueryBehavior = autoWidgetQueryBehavior;
    widgetRequest.widgetQueryBehavior = widgetQueryBehavior;
    widgetRequest.widgetTemplateQueryBehavior = widgetTemplateQueryBehavior;

    props.saveWidgetDataAndRequests({settings: settingsState}, [widgetRequest]).then(() => {
      if (props.widgetRefresh) {
        props.widgetRefresh();
      }
    })
  }

  const [tabValue, setTabValue] = useState<number>(0);
  const handleTabChange = (event: React.SyntheticEvent<Element, Event>, newValue: number) => setTabValue(newValue);

  return (
    <TabbedDialog
      currentTab={tabValue}
      handleTabChange={handleTabChange}
      onCancel={() => props.setSettingsDialogIsOpen(false)}
      onConfirm={() => {
        updateAggs();
        props.setSettingsDialogIsOpen(false);
      }}
      open={true}
      tabs={[i18n.t("data"), i18n.t("display")]}
      title={i18n.t("widget-types.numeric-card.settings.title")}>
      <>
        {tabValue === 0 && <>
          {props.queryInfo && <>
            <SimpleFormLabel>{i18n.t("category")}</SimpleFormLabel>
            <LeafAggSelector
              allowedFieldTypes={["decimal", "integer", "keyword", "keywords"]}
              queryInfo={props.queryInfo}
              aggs={localAggs}
              setAggs={(newAggs: Agg | null) => setLocalAggs(newAggs || undefined)}
              aggsLabel={aggsLabel}
              setAggsLabel={setAggsLabel}
              ></LeafAggSelector>
            <MultiQueryRequestSettings
              presetOptionsType={MultiQueryRequestSettingsPresets.AUTO_SINGLE}

              autoWidgetQueryBehavior={autoWidgetQueryBehavior}
              setAutoWidgetQueryBehavior={(behavior: AutoWidgetQueryBehavior) => setAutoWidgetQueryBehavior(behavior)}
              widgetQueryBehavior={widgetQueryBehavior}
              setWidgetQueryBehavior={(behavior: WidgetQueryBehavior) => setWidgetQueryBehavior(behavior)}
              widgetTemplateQueryBehavior={widgetTemplateQueryBehavior}
              setWidgetTemplateQueryBehavior={(behavior: WidgetTemplateQueryBehavior) => setWidgetTemplateQueryBehavior(behavior)} />
          </>}
          {(!props.queryInfo) &&
            <QueryWidgetSettingsMissingQueryInfoAlert />
          }
        </>}
        {tabValue === 1 &&
          <SplitSettingsWrapper>
            {/* Widget Preview */}
            <NumericCardPreview settingsState={settingsState} />
            {/* Settings Options */}
            <SettingsOptions onChange={settingsOnChange} settingsState={settingsState}>
              <SettingsAccordion label={i18n.t("widget-settings.widget-title")} showBottomDivider={true}>
                <SettingsWidgetTitleSelect
                  keyid="widgetTitle"
                  label={i18n.t("title")}
                  showQueryOption={true}
                  widgetTitleValue={settingsState.widgetTitle} />
                  {settingsState.widgetTitle === 'custom' &&
                    <Stack direction="row" spacing={2} sx={{ marginLeft: 2 }}>
                      <Divider flexItem orientation="vertical" sx={{ width: "5px" }} />
                      <SettingsTextField
                        keyid="customWidgetTitle"
                        label={i18n.t("widget-settings.custom-text")}
                        value={settingsState.customWidgetTitle}
                      />
                    </Stack>}
                {/* Widget Title Alignment */}
                <SettingsToggleButton
                  keyid="widgetTitleAlignment"
                  label={i18n.t("widget-settings.alignment")}
                  optionsList={horizontalAlignmentOptions}
                  value={settingsState.widgetTitleAlignment} />
              </SettingsAccordion>
              <SettingsColorPicker
                color={settingsState.color ?? undefined}
                defaultColor={undefined}
                keyid="color"
                label={i18n.t("color")} />
              <SettingsCheckbox
                checked={settingsState.abbreviate}
                keyid="abbreviate"
                label={i18n.t("widget-types.numeric-card.settings.abbreviate")} />
              <SettingsCheckbox
                checked={settingsState.showDecimals}
                keyid="showDecimals"
                label={i18n.t("widget-types.numeric-card.settings.show-decimals")} />
              {/* Widget Text Alignment */}
              <SettingsToggleButton
                  keyid="alignment"
                  label={i18n.t("widget-settings.alignment")}
                  optionsList={horizontalAlignmentOptions}
                  value={settingsState.alignment} />
            </SettingsOptions>
          </SplitSettingsWrapper>}
      </>
    </TabbedDialog>
  )
}
